import { gunzipSync, strFromU8 } from 'fflate';

export interface TarItem {
  name: string;
  buffer: ArrayBuffer;
}

/**
 * Reads the `path` record from a PAX extended header block, which holds the real (long) name of the entry that
 * follows it. PAX records are formatted as `"<length> <key>=<value>\n"`, concatenated. Returns `undefined` when
 * there is no `path` record. Paths in our archives are ASCII, so byte and character lengths line up.
 */
function readPaxPath(content: Uint8Array): string | undefined {
  const text = strFromU8(content);
  let i = 0;
  while (i < text.length) {
    const space = text.indexOf(' ', i);
    if (space < 0) {
      break;
    }
    const length = parseInt(text.slice(i, space), 10);
    if (!Number.isFinite(length) || length <= 0) {
      break;
    }
    const record = text.slice(space + 1, i + length - 1); // exclude the trailing newline
    const eq = record.indexOf('=');
    if (eq >= 0 && record.slice(0, eq) === 'path') {
      return record.slice(eq + 1);
    }
    i += length;
  }
  return undefined;
}

/**
 * Parses the content of a (decompressed) TAR archive and returns its regular file entries.
 *
 * This is a minimal, dependency-free reader that works in both the browser and Node.js. It handles long entry
 * names from the USTAR `prefix` field, GNU long name (`L` type flag) entries, and PAX extended headers (`x` type
 * flag), and skips directories and other non-regular entries.
 *
 * @param data - The decompressed TAR archive content.
 * @returns The regular file entries with their name and content.
 */
export function readTarItems(data: Uint8Array): TarItem[] {
  const items: TarItem[] = [];
  const readString = (offset: number, length: number) =>
    strFromU8(data.subarray(offset, offset + length)).replace(/\0.*$/, '');

  let offset = 0;
  // Real name of the next entry, supplied by a preceding GNU 'L' or PAX 'x' header.
  let overrideName: string | null = null;

  while (offset + 512 <= data.length) {
    // A zero-filled block marks the end of the archive.
    let allZero = true;
    for (let i = 0; i < 512; i++) {
      if (data[offset + i] !== 0) {
        allZero = false;
        break;
      }
    }
    if (allZero) {
      break;
    }

    let name = readString(offset, 100);
    const prefix = readString(offset + 345, 155);
    if (prefix) {
      name = `${prefix}/${name}`;
    }
    const size = parseInt(readString(offset + 124, 12).trim(), 8) || 0;
    const typeFlag = String.fromCharCode(data[offset + 156]);

    offset += 512;
    const content = data.subarray(offset, offset + size);

    if (typeFlag === 'L') {
      // GNU long name: the entry content holds the real name of the following entry.
      overrideName = strFromU8(content).replace(/\0.*$/, '');
    } else if (typeFlag === 'x') {
      // PAX extended header: the real path of the following entry is in its `path` record.
      const paxPath = readPaxPath(content);
      if (paxPath) {
        overrideName = paxPath;
      }
    } else if (typeFlag === 'g') {
      // PAX global extended header: not relevant to individual entry names.
    } else {
      if (overrideName) {
        name = overrideName;
        overrideName = null;
      }
      // Collect regular files only ('0' and '\0' are regular file type flags).
      if (name && (typeFlag === '0' || typeFlag === '\0' || typeFlag === '')) {
        items.push({ name, buffer: content.slice().buffer });
      }
    }

    // File content is padded to a multiple of 512 bytes.
    offset += Math.ceil(size / 512) * 512;
  }

  return items;
}

/**
 * Unpacks the .tar.gz (odbx format) file and returns the items.
 *
 * Works in both the browser and Node.js: decompression uses `fflate` and the TAR archive is read by
 * {@link readTarItems}, so it does not depend on any browser-only API.
 *
 * @param data - The .tar.gz (odbx format) file.
 * @returns - The items in the .tar.gz file.
 */
export async function unpackBundle(data: Blob | ArrayBuffer) {
  const buffer = data instanceof ArrayBuffer ? data : await data.arrayBuffer();
  const archive = gunzipSync(new Uint8Array(buffer));
  const items = readTarItems(archive);

  const hasCache = items.some((item) => item.name.startsWith('cache/'));

  let images = [];
  const manifestJson = items.find((item) => item.name.match(/manifest.json$/));
  if (manifestJson) {
    const content = strFromU8(new Uint8Array(manifestJson.buffer));
    const manifest = JSON.parse(content);
    images = manifest.map((m: any) => m.RepoTags).reduce((a: any[], b: any[]) => a.concat(b), []);
  }

  const packages = items.filter((item) => item.name.match(/\/package(.*).zip/));

  return {
    hasCache,
    images,
    items,
    packages,
  };
}
